/** Local capture types + seed data for Catalogue governance badges (approved/blocked,
 * off-contract spend, pending price-change approval, site-specific availability).
 * UI-only for the demo - surfaced as badges/chips on Catalogues/Categories/Products list
 * views, keyed by itemNumber. No backend persistence exists for these fields yet. */

export enum CatalogueItemGovernanceStatus {
  Approved = 'Approved',
  Blocked = 'Blocked',
  PendingPriceApproval = 'PendingPriceApproval',
  OffContract = 'OffContract',
}

export interface CatalogueGovernanceFlag {
  itemNumber: string;
  status: CatalogueItemGovernanceStatus;
  reason?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  siteRestriction: 'AllSites' | 'SelectedSites';
  siteNumbers?: string[];
  offContractSpendYtd?: number;
}

export const catalogueGovernanceFlags: CatalogueGovernanceFlag[] = [
  {
    itemNumber: 'BTR-002',
    status: CatalogueItemGovernanceStatus.PendingPriceApproval,
    reason: 'Supplier price increase of 8% submitted - awaiting commercial approval.',
    effectiveFrom: '2026-08-01',
    siteRestriction: 'AllSites',
  },
  {
    itemNumber: 'SPC-009',
    status: CatalogueItemGovernanceStatus.Blocked,
    reason: 'Ingredient recall in effect - blocked from ordering pending supplier confirmation.',
    siteRestriction: 'AllSites',
  },
  {
    itemNumber: 'OLV-002',
    status: CatalogueItemGovernanceStatus.OffContract,
    reason: 'Purchased outside the agreed catalogue/contract - flagged for review.',
    siteRestriction: 'SelectedSites',
    siteNumbers: ['0145'],
    offContractSpendYtd: 1240.5,
  },
  {
    itemNumber: 'CRS-100',
    status: CatalogueItemGovernanceStatus.Approved,
    siteRestriction: 'SelectedSites',
    siteNumbers: ['0101', '0102', '0145'],
  },
];

export function getGovernanceFlag(itemNumber: string): CatalogueGovernanceFlag | undefined {
  return catalogueGovernanceFlags.find((flag) => flag.itemNumber === itemNumber);
}

/**
 * Row-position fallback pattern so the Products list always visibly demonstrates catalogue
 * governance variety even when the real, backend-driven item list doesn't happen to contain
 * any of the specific fictional item numbers above (which it usually won't - the demo
 * doesn't control the real catalogue's item numbers). Every 5th/8th/11th row (by position,
 * 0-indexed) gets a non-approved status; everything else (including any real exact-itemNumber
 * match above) is unaffected.
 */
export function getGovernanceFlagForRow(itemNumber: string, rowIndex: number): CatalogueGovernanceFlag {
  const exactMatch = getGovernanceFlag(itemNumber);
  if (exactMatch) return exactMatch;

  const position = rowIndex % 8;
  if (position === 4) {
    return {
      itemNumber, status: CatalogueItemGovernanceStatus.Blocked,
      reason: 'Ingredient recall in effect - blocked from ordering pending supplier confirmation.',
      siteRestriction: 'AllSites',
    };
  }
  if (position === 6) {
    return {
      itemNumber, status: CatalogueItemGovernanceStatus.PendingPriceApproval,
      reason: 'Supplier price increase submitted - awaiting commercial approval.',
      siteRestriction: 'AllSites',
    };
  }
  if (position === 2) {
    return {
      itemNumber, status: CatalogueItemGovernanceStatus.OffContract,
      reason: 'Purchased outside the agreed catalogue/contract - flagged for review.',
      siteRestriction: 'AllSites', offContractSpendYtd: 340.0,
    };
  }
  return { itemNumber, status: CatalogueItemGovernanceStatus.Approved, siteRestriction: 'AllSites' };
}

